import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ReimbursementShieldIcon } from './FlexiOssCinematic/PhilHealthIcons';
import { AnimatedIcon } from './FlexiOssCinematic/AnimatedIcon';

export const PhilHealthCallout = () => {
  const { theme } = useTheme();
  const dark = theme !== 'light';

  const shell = dark
    ? 'bg-gradient-to-br from-[#04291b] via-slate-900 to-slate-900 border-[#006837]/45 shadow-[0_0_50px_-16px_rgba(0,104,55,0.45)]'
    : 'bg-gradient-to-br from-white to-[#eef7f1] border-[#006837]/20 shadow-[0_12px_40px_-14px_rgba(0,104,55,0.18)]';
  const heading = dark ? 'text-[#36d27e]' : 'text-[#006837]';
  const sub = dark ? 'text-slate-300' : 'text-[#41584b]';
  const badge = dark
    ? 'bg-[#FFC72C]/12 text-[#FFC72C] ring-1 ring-[#FFC72C]/25'
    : 'bg-[#FFC72C]/25 text-[#7a5e00] ring-1 ring-[#FFC72C]/40';

  return (
    <motion.div
      data-testid="philhealth-callout"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className={`group relative my-8 overflow-hidden border p-6 sm:p-8 ${shell}`}
    >
      {/* gold → green hairline */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#006837] via-[#FFC72C] to-transparent" />
      {/* ambient orb */}
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#006837]/20 blur-3xl" />

      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center bg-[#006837] text-[#ffffff] shadow-lg shadow-[#006837]/40 ring-1 ring-[#FFC72C]/30">
            <AnimatedIcon><ReimbursementShieldIcon className="h-6 w-6" strokeWidth={1.6} /></AnimatedIcon>
          </div>
          <div>
            <span className={`mb-2 inline-block px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${badge}`}>
              PhilHealth Coverage
            </span>
            <h4 className={`text-lg font-extrabold leading-tight tracking-tight sm:text-xl ${heading}`}>
              PhilHealth Subsidy &amp; Co-Payment Guide
            </h4>
            <p className={`mt-1.5 max-w-md text-sm leading-relaxed ${sub}`}>
              Understand your PhilHealth subsidy allowance and co-payment options for procedures that use FlexiOss® — written for both patients and clinicians.
            </p>
          </div>
        </div>

        <Link
          to="/reimbursement-guide"
          data-testid="philhealth-callout-btn"
          className="inline-flex flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-none bg-[#006837] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-[#ffffff] ring-1 ring-[#FFC72C]/40 transition-all duration-300 hover:bg-[#00532c] hover:shadow-[0_10px_30px_-8px_rgba(0,104,55,0.6)]"
        >
          View PhilHealth Codes
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
        </Link>
      </div>
    </motion.div>
  );
};

export default PhilHealthCallout;
